<script setup>
import { ref } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import AccordionItem from "../ui/AccordionItem.vue";
import BaseButton from "../ui/BaseButton.vue";
import IconMail from "../icons/IconMail.vue";
import { useLanding } from "../../composables/useLanding";
import { site } from "../../content/site";
import { useMediaQuery } from "../../composables/useMediaQuery";
import { track } from "../../composables/useAnalytics";
import { scrollToLeadForm } from "../../composables/useScrollTo";

const faq = useLanding().faq;

// Side-by-side from here up; the heading aligns to its column.
const isWide = useMediaQuery("(min-width: 960px)");

// One open at a time. Clicking the open one closes it.
const openId = ref(faq[0]?.id ?? null);

function toggle(item) {
  const opening = openId.value !== item.id;
  openId.value = opening ? item.id : null;
  if (opening) track("faq_open", { question: item.id });
}
</script>

<template>
  <section class="section section--canvas" id="faq">
    <div class="container faq-layout">
      <aside class="faq-aside">
        <SectionHeading
          eyebrow="שאלות נפוצות"
          title="מה שבדרך כלל שואלים אותנו"
          :align="isWide ? 'start' : 'center'"
        />

        <div class="faq-contact">
          <p class="faq-contact__title">לא מצאתם תשובה?</p>
          <p class="faq-contact__text">
            השאירו פרטים ונחזור אליכם עם כל מה שתרצו לדעת.
          </p>
          <BaseButton variant="inverse" @click="scrollToLeadForm('faq')">
            שאלו אותנו
          </BaseButton>
          <a class="faq-contact__mail" :href="`mailto:${site.email}`">
            <IconMail aria-hidden="true" />
            <span>{{ site.email }}</span>
          </a>
        </div>
      </aside>

      <div class="faq">
        <AccordionItem
          v-for="item in faq"
          :key="item.id"
          :id="item.id"
          :question="item.question"
          :open="openId === item.id"
          @toggle="toggle(item)"
        >
          {{ item.answer }}
        </AccordionItem>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq-layout {
  display: grid;
  gap: var(--space-6);
}

@media (min-width: 960px) {
  .faq-layout {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: var(--space-9);
    align-items: start;
  }

  .faq-aside {
    position: sticky;
    inset-block-start: calc(var(--nav-height) + var(--space-6));
  }
}

/*
 * One list, ruled between questions (AccordionItem draws the rules). The
 * open question takes the brand edge the hero's booked slot carries.
 */
.faq :deep(.accordion) {
  padding-inline-start: var(--space-4);
  border-inline-start: 4px solid transparent;
  transition: border-color var(--dur-base) var(--ease-in-out);
}

.faq :deep(.accordion.is-open) {
  border-inline-start-color: var(--brand);
}

/* ------------------------------------------------------------- contact */

.faq-contact {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-6);
  border-radius: var(--radius-2xl);
  background: var(--ink);
  color: var(--on-ink-muted);
}

.faq-contact__title {
  margin: 0;
  color: var(--on-ink);
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
}

.faq-contact__text {
  margin: 0 0 var(--space-2);
}

.faq-contact__mail {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-block-start: var(--space-2);
  color: var(--on-ink-muted);
  font-size: var(--text-sm);
  text-decoration: none;
  transition: color var(--dur-fast) var(--ease-in-out);
}

.faq-contact__mail:hover {
  color: var(--on-ink);
}

.faq-contact__mail :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
}

/* On phones the contact card follows the questions. */
@media (max-width: 959px) {
  .faq-aside {
    display: contents;
  }

  .faq-contact {
    order: 2;
    align-items: center;
    text-align: center;
  }
}
</style>
